/**
 * "Build" tab — the Agent Builder: browse the user's agent sets and standalone
 * agents, and visualise the selected one as a graph (members + transfer/subagent
 * links). "Build with AI" opens the conversational builder.
 *
 * User-facing terminology note: a "set" is shown to users as an "Agent Team",
 * and a standalone (set-less) agent as a "Simple Agent". The code, API and DB
 * construct is still a "set", so identifiers and comments keep that name — only
 * the strings rendered to the user say "team"/"Agent Builder".
 */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListDivider from '@mui/joy/ListDivider';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HubIcon from '@mui/icons-material/Hub';
import EditIcon from '@mui/icons-material/Edit';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AgentSetGraph from '../sets/AgentSetGraph';
import SetBuilderChat from '../sets/SetBuilderChat';
import TelephonyResourceSelector from '../agent/TelephonyResourceSelector';
import ErrorBoundary from '../common/ErrorBoundary';
import { listAgentSets, getAgentSet, promoteAgentToSet, deleteAgentSet } from '../../api/sets';
import { getAgent } from '../../api/agent';
import useLazyAgents from '../../hooks/useLazyAgents';
import useListboxPaging from '../../hooks/useListboxPaging';

// The test drawer pulls in the WebRTC widget; load it on demand so that heavy
// dependency stays out of the main bundle until someone tests an agent.
const TestAgentDrawer = lazy(() => import('../sets/TestAgentDrawer'));
// The edit modal pulls in the agent field components + functions editor; load
// on demand so they stay out of the main bundle until someone edits an agent.
const AgentEditModal = lazy(() => import('../sets/AgentEditModal'));
// The telephone-test drawer streams a live call transcript; load on demand.
const TelephoneTestDrawer = lazy(() => import('../sets/TelephoneTestDrawer'));

function formatApiError(err) {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && data.message) return data.message;
  if (typeof data === 'string') return data;
  return err?.message || 'Request failed';
}

/** A set-member label (unique within a set, alphanumeric) derived from an agent name. */
function slugLabel(name) {
  const s = String(name || 'agent').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
  return s || 'agent';
}

/** Download a set document as `<name>.json`. */
function downloadSetJson(set) {
  const name = (set?.name || 'agent-set').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'agent-set';
  const blob = new Blob([JSON.stringify(set, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

function Legend() {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Chip size="sm" variant="solid" color="primary">main agent</Chip>
      <Chip size="sm" variant="soft" color="success">voice</Chip>
      <Chip size="sm" variant="soft" color="warning">text</Chip>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <Box sx={{ width: 22, height: 0, borderTop: '2px solid var(--joy-palette-primary-500)' }} />
        <Typography level="body-xs">transfer</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
        <Box sx={{ width: 22, height: 0, borderTop: '2px dashed var(--joy-palette-warning-600)' }} />
        <Typography level="body-xs">subagent</Typography>
      </Stack>
    </Stack>
  );
}

export default function SetsPanel({ setError, setInform, status, troubleshootSeed, setTroubleshootSeed }) {
  const [sets, setSets] = useState([]);
  // Ids of agents that already belong to a set — excluded from the lazily loaded
  // "Simple Agents" list (there is no server-side set-less filter).
  const [memberIds, setMemberIds] = useState(() => new Set());
  // Whether the team/agent dropdown is open — gates the lazy Simple Agents fetch.
  const [selectOpen, setSelectOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [set, setSet] = useState(null);
  const [setLoading, setSetLoading] = useState(false);
  const [promoting, setPromoting] = useState(false); // promoting a synthetic singleton into a real set
  const [deleting, setDeleting] = useState(false); // deleting the selected real set
  const [deleteStep, setDeleteStep] = useState(0); // 0 closed · 1 first confirm · 2 final confirm (double-confirm gate)
  const [builderSeed, setBuilderSeed] = useState(null); // null | { set, testResult } → opens the AI builder
  const [chooser, setChooser] = useState(null); // null | { id, name, agentType, testable } → node-click action picker
  const [editMember, setEditMember] = useState(null); // null | member object → opens the edit modal
  const [phoneTestAgent, setPhoneTestAgent] = useState(null); // null | { id, name } → opens the telephony resource picker
  const [phoneTest, setPhoneTest] = useState(null); // null | { agent, resource } → opens the telephone-test drawer
  const [testAgent, setTestAgent] = useState(null);
  const [lastTestResult, setLastTestResult] = useState(null); // most recent node-click test, for "Troubleshoot with AI"

  // Lazily loaded "Simple Agents" (set-less), 20 then 10 at a time, holding only
  // {id,name}. Fetches nothing until the dropdown opens; set members are filtered
  // out client-side per page (no server-side set-less filter exists).
  const {
    agents: simpleAgents,
    loading: simpleLoading,
    loadingMore: simpleLoadingMore,
    hasLoaded: simpleHasLoaded,
    hasMore: simpleHasMore,
    loadMore: loadMoreSimple,
  } = useLazyAgents({ enabled: selectOpen, excludeIds: memberIds, searchField: 'name' });

  // Auto-load more simple agents on scroll-to-bottom, preserving scroll position.
  const { listboxRef: simpleListboxRef, onScroll: onSimpleScroll } = useListboxPaging({
    hasMore: simpleHasMore,
    busy: simpleLoading || simpleLoadingMore,
    loadMore: loadMoreSimple,
    itemCount: simpleAgents.length,
  });

  // Resolve a clicked graph node back to its full member definition in the
  // loaded set (node ids are the member's agent id, or `label:<label>` for an
  // unsaved draft member).
  const memberByNodeId = useCallback(
    (id) => (set?.agents || []).find((a) => (a.id || `label:${a.label}`) === id) || null,
    [set],
  );

  // Telephony handler (jambonz/livekit/…) parsed from the member's modelName, used
  // to filter the phone numbers / registrations the resource picker offers.
  const agentHandlerForNode = useCallback((id) => {
    const match = memberByNodeId(id)?.modelName?.match(/^([a-z0-9-_]+):/);
    return match ? match[1] : null;
  }, [memberByNodeId]);

  const refresh = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await listAgentSets();
      const setList = Array.isArray(data?.agentSets) ? data.agentSets : [];
      setSets(setList);
      // Collect member ids so the lazily loaded "Simple Agents" list can exclude
      // agents that already belong to a set (no server-side set-less filter).
      const ids = new Set();
      setList.forEach((s) => (s.agents || []).forEach((a) => a?.id && ids.add(a.id)));
      setMemberIds(ids);
    } catch (err) {
      setError?.(`Failed to load agent teams: ${formatApiError(err)}`);
    } finally {
      setListLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (status === 'loggedIn') refresh();
  }, [status, refresh]);

  // "Troubleshoot with AI" handed over from the Observe tab: find the set the
  // call's agent belongs to, load it, and open the AI builder seeded with the
  // call's transcripts + invocation logs — the same context the post-test
  // troubleshoot uses. If the agent isn't in a set, the builder still opens with
  // just the diagnostics. The seed is consumed so it doesn't re-open on re-render.
  useEffect(() => {
    if (!troubleshootSeed) return undefined;
    let cancelled = false;
    (async () => {
      const { agentId, testResult } = troubleshootSeed;
      let full = null;
      try {
        const data = await listAgentSets();
        const summary = (data?.agentSets || []).find((s) => (s.agents || []).some((a) => a.id === agentId));
        if (summary?.id) full = await getAgentSet(summary.id);
      } catch (err) {
        setError?.(`Couldn't load the agent's team: ${formatApiError(err)}`);
      }
      if (cancelled) return;
      if (full) { setSelectedId(full.id); setSet(full); }
      setBuilderSeed({ set: full, testResult });
      setTroubleshootSeed?.(null);
    })();
    return () => { cancelled = true; };
  }, [troubleshootSeed, setTroubleshootSeed, setError]);

  const loadSet = useCallback(async (id) => {
    setSelectedId(id);
    if (!id) { setSet(null); return; }
    setSetLoading(true);
    try {
      setSet(await getAgentSet(id));
    } catch (err) {
      setError?.(`Failed to load team: ${formatApiError(err)}`);
      setSet(null);
    } finally {
      setSetLoading(false);
    }
  }, [setError]);

  // Synthesise a non-persisted single-member set so a legacy set-less agent flows
  // through the same graph / edit / test surfaces as a real set member. The agent
  // itself is real (its id drives edit/test/telephone); only the set wrapper is
  // synthetic, marked so set-level actions (AI builder, set delete) are gated.
  const loadSingletonAgent = useCallback(async (agentId) => {
    const syntheticId = `agent:${agentId}`;
    setSelectedId(syntheticId);
    setSetLoading(true);
    try {
      const summary = simpleAgents.find((a) => a.id === agentId) || {};
      const full = await getAgent(agentId);
      const modelName = full?.modelName || summary.modelName || null;
      const name = full?.name || summary.name || agentId;
      const member = {
        id: agentId,
        label: slugLabel(name),
        name,
        description: full?.description ?? summary.description ?? null,
        type: full?.type || (/^text:/i.test(String(modelName || '')) ? 'text' : 'interactive-audio'),
        modelName,
        prompt: full?.prompt,
        options: full?.options,
        functions: full?.functions,
        mcpServers: full?.mcpServers,
      };
      setSet({ id: syntheticId, name, synthetic: true, agents: [member] });
    } catch (err) {
      setError?.(`Failed to load agent: ${formatApiError(err)}`);
      setSet(null);
    } finally {
      setSetLoading(false);
    }
  }, [simpleAgents, setError]);

  // The dropdown mixes real sets (UUID values) and set-less agents (`agent:<id>`).
  const handleSelect = useCallback((value) => {
    if (!value) { loadSet(null); return; }
    if (String(value).startsWith('agent:')) { loadSingletonAgent(String(value).slice('agent:'.length)); return; }
    loadSet(value);
  }, [loadSet, loadSingletonAgent]);

  // Re-load whatever is selected (real set or synthetic agent), e.g. after an
  // in-place member edit. A synthetic id must NOT hit getAgentSet (it would 404).
  const reloadSelected = useCallback(async () => {
    if (!selectedId) return;
    if (String(selectedId).startsWith('agent:')) await loadSingletonAgent(String(selectedId).slice('agent:'.length));
    else await loadSet(selectedId);
  }, [selectedId, loadSet, loadSingletonAgent]);

  // Promote a synthetic singleton into a real persisted set: the backend MOVES the
  // existing agent into a new set (keeps its id/deployments/history). Then switch
  // the view to the now-real set and refresh the lists.
  const handlePromote = useCallback(async () => {
    const agentId = String(selectedId || '').startsWith('agent:') ? String(selectedId).slice('agent:'.length) : null;
    if (!agentId) return;
    setPromoting(true);
    try {
      const created = await promoteAgentToSet(agentId, set?.name ? { name: set.name } : {});
      await refresh();
      if (created?.id) { setSelectedId(created.id); setSet(created); }
      setInform?.({ success: 'Agent promoted to a team.' });
    } catch (err) {
      setError?.(`Couldn't promote to a team: ${formatApiError(err)}`);
    } finally {
      setPromoting(false);
    }
  }, [selectedId, set, refresh, setError, setInform]);

  // Delete the selected real set. Double-confirmed (two escalating steps) because
  // the backend removes the set AND every member agent (prompts, functions, config
  // and their deployments) in one transaction (agent-sets/{agentSetId} DELETE).
  // Past calls are NOT deleted — Call.agentId is SET NULL — so history is retained.
  // Clears the selection and refreshes the list afterwards.
  const handleDelete = useCallback(async () => {
    if (!selectedId || set?.synthetic) return;
    setDeleting(true);
    try {
      await deleteAgentSet(selectedId);
      setInform?.({ success: `Deleted “${set?.name || 'team'}”.` });
      setDeleteStep(0);
      setSelectedId(null);
      setSet(null);
      await refresh();
    } catch (err) {
      setError?.(`Couldn't delete: ${formatApiError(err)}`);
    } finally {
      setDeleting(false);
    }
  }, [selectedId, set, refresh, setError, setInform]);

  const memberCount = set?.agents?.length ?? 0;
  const isSynthetic = !!set?.synthetic;

  if (builderSeed) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <SetBuilderChat
          setError={setError}
          initialSet={builderSeed.set}
          initialTestResult={builderSeed.testResult}
          onClose={async (createdSet) => {
            setBuilderSeed(null);
            await refresh();
            if (createdSet?.id) {
              setSelectedId(createdSet.id);
              setSet(createdSet);
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <HubIcon />
            <Typography level="title-lg">Agent Builder</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Select
              placeholder={listLoading ? 'Loading…' : 'Select a team or agent…'}
              value={selectedId}
              onChange={(_e, v) => handleSelect(v)}
              listboxOpen={selectOpen}
              onListboxOpenChange={setSelectOpen}
              sx={{ minWidth: 220 }}
              disabled={listLoading}
              slotProps={{ listbox: { ref: simpleListboxRef, onScroll: onSimpleScroll, sx: { maxHeight: 360, overflow: 'auto' } } }}
            >
              {sets.length > 0 && (
                <List aria-labelledby="sets-group-label">
                  <ListItem id="sets-group-label" sticky>
                    <Typography level="body-xs" textTransform="uppercase" sx={{ color: 'text.tertiary' }}>Agent Teams (sets)</Typography>
                  </ListItem>
                  {sets.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name || s.id} ({s.agents?.length ?? 0})
                    </Option>
                  ))}
                </List>
              )}
              {sets.length > 0 && <ListDivider role="none" />}
              <List aria-labelledby="agents-group-label">
                <ListItem id="agents-group-label" sticky>
                  <Typography level="body-xs" textTransform="uppercase" sx={{ color: 'text.tertiary' }}>Simple Agents</Typography>
                </ListItem>
                {/* Keep the selected simple agent's Option mounted even when it's
                    off the current page, so the trigger keeps its label after the
                    dropdown closes (Joy skips renderValue for an unmounted option).
                    Its name comes from the loaded synthetic set. */}
                {selectedId &&
                  String(selectedId).startsWith('agent:') &&
                  !simpleAgents.some((a) => `agent:${a.id}` === selectedId) && (
                    <Option key={selectedId} value={selectedId}>
                      {set?.name || selectedId.slice('agent:'.length)}
                    </Option>
                  )}
                {simpleAgents.map((a) => (
                  <Option key={`agent:${a.id}`} value={`agent:${a.id}`}>
                    {a.name || a.id}
                  </Option>
                ))}
                {(simpleLoading || !simpleHasLoaded) && (
                  <ListItem sx={{ justifyContent: 'center', py: 1 }}>
                    <CircularProgress size="sm" />
                  </ListItem>
                )}
                {simpleHasLoaded && !simpleLoading && !simpleHasMore && simpleAgents.length === 0 && (
                  <ListItem>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>No simple agents.</Typography>
                  </ListItem>
                )}
                {simpleHasMore && (
                  <ListItem
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ justifyContent: 'center', py: 0.5 }}
                  >
                    <Button
                      size="sm"
                      variant="plain"
                      loading={simpleLoadingMore}
                      onMouseDown={(e) => {
                        // Keep listbox focus so it doesn't blur-close or scroll-reset.
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        loadMoreSimple();
                      }}
                    >
                      Load more
                    </Button>
                  </ListItem>
                )}
              </List>
            </Select>
            <IconButton variant="outlined" color="neutral" onClick={refresh} disabled={listLoading} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<FileDownloadIcon />}
              disabled={!set}
              onClick={() => set && downloadSetJson(isSynthetic ? { name: set.name, agents: set.agents } : set)}
            >
              Download
            </Button>
            {lastTestResult && (
              <Button
                variant="solid"
                color="warning"
                startDecorator={<AutoAwesomeIcon />}
                onClick={() => { setBuilderSeed({ set: isSynthetic ? null : set, testResult: lastTestResult }); setLastTestResult(null); }}
              >
                Troubleshoot with AI
              </Button>
            )}
            {isSynthetic ? (
              <Button
                variant="solid"
                color="primary"
                startDecorator={<HubIcon />}
                loading={promoting}
                onClick={handlePromote}
              >
                Promote to team
              </Button>
            ) : (
              <Button
                variant="solid"
                color="primary"
                startDecorator={<AutoAwesomeIcon />}
                onClick={() => setBuilderSeed({ set: set || null, testResult: null })}
              >
                {set ? 'Edit with AI' : 'Build with AI'}
              </Button>
            )}
            {set && !isSynthetic && (
              <Button
                variant="outlined"
                color="danger"
                startDecorator={<DeleteForeverIcon />}
                onClick={() => setDeleteStep(1)}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>

        <Sheet variant="outlined" sx={{ borderRadius: 'sm', minHeight: 560, p: { xs: 1, sm: 2 } }}>
          {setLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 520 }}>
              <CircularProgress />
            </Box>
          ) : set ? (
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography level="title-md" noWrap>{set.name || 'Untitled team'}</Typography>
                  {set.description && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{set.description}</Typography>
                  )}
                </Box>
                {isSynthetic
                  ? <Chip size="sm" variant="soft" color="neutral">simple agent</Chip>
                  : <Chip size="sm" variant="soft">{memberCount} agent{memberCount === 1 ? '' : 's'}</Chip>}
              </Stack>
              <Legend />
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                Tip: click an agent to edit its configuration, or test a voice agent in your browser.
              </Typography>
              <ErrorBoundary resetKey={selectedId} title="Couldn’t draw this graph.">
                <AgentSetGraph set={set} onSelectAgent={setChooser} />
              </ErrorBoundary>
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 520, gap: 1.5, textAlign: 'center' }}>
              <HubIcon sx={{ fontSize: 48, color: 'text.tertiary' }} />
              <Typography level="title-md">Nothing selected</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 420 }}>
                {sets.length
                  ? 'Choose a team or agent above to see how its agents hand calls to one another.'
                  : 'You have no agent teams yet. Build one with AI, or create one via the API, then it will appear here.'}
              </Typography>
            </Box>
          )}
        </Sheet>
      </Stack>

      {/* Node-click action picker: edit any member, or browser-test a voice one. */}
      <Modal open={!!chooser} onClose={() => setChooser(null)}>
        <ModalDialog sx={{ maxWidth: 380 }}>
          <ModalClose />
          <DialogTitle>{chooser?.name || 'Agent'}</DialogTitle>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 1 }}>
            What would you like to do?
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<EditIcon />}
              onClick={() => {
                const member = memberByNodeId(chooser.id);
                setChooser(null);
                if (member) setEditMember(member);
                else setError?.('Could not find this agent in the loaded set.');
              }}
            >
              Edit details
            </Button>
            {chooser?.testable && (
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<RecordVoiceOverIcon />}
                onClick={() => {
                  setTestAgent({ id: chooser.id, name: chooser.name });
                  setChooser(null);
                }}
              >
                Test in browser
              </Button>
            )}
            {chooser?.testable && (
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<PhoneInTalkIcon />}
                onClick={() => {
                  setPhoneTestAgent({ id: chooser.id, name: chooser.name });
                  setChooser(null);
                }}
              >
                Telephone test
              </Button>
            )}
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Delete a set — double-confirmed, since this also permanently removes
          every member agent (their config + deployments). Past calls are kept. */}
      <Modal open={deleteStep > 0} onClose={() => !deleting && setDeleteStep(0)}>
        <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 440 }}>
          <ModalClose disabled={deleting} />
          <DialogTitle>
            <DeleteForeverIcon color="danger" />
            Delete “{set?.name || 'team'}”?
          </DialogTitle>
          {deleteStep === 1 ? (
            <>
              <Typography level="body-sm">
                This permanently deletes the team and its {memberCount} member
                agent{memberCount === 1 ? '' : 's'} — their prompts, functions and
                configuration, along with any active deployments. Past call records
                are retained. This cannot be undone.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="plain" color="neutral" onClick={() => setDeleteStep(0)}>Cancel</Button>
                <Button variant="solid" color="danger" onClick={() => setDeleteStep(2)}>Continue</Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography level="body-sm">
                Are you absolutely sure? Deleting <strong>{set?.name || 'this team'}</strong>{' '}
                and its {memberCount} agent{memberCount === 1 ? '' : 's'} is immediate and
                irreversible.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="plain" color="neutral" disabled={deleting} onClick={() => setDeleteStep(0)}>Cancel</Button>
                <Button
                  variant="solid"
                  color="danger"
                  loading={deleting}
                  startDecorator={<DeleteForeverIcon />}
                  onClick={handleDelete}
                >
                  Delete permanently
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      {/* Telephone test: pick a number/registration, then stream the inbound call. */}
      <TelephonyResourceSelector
        open={!!phoneTestAgent}
        agentHandler={phoneTestAgent ? agentHandlerForNode(phoneTestAgent.id) : null}
        onClose={() => setPhoneTestAgent(null)}
        onSelect={(resource) => setPhoneTest({ agent: phoneTestAgent, resource })}
      />

      {phoneTest && (
        <ErrorBoundary resetKey={phoneTest.agent?.id} onError={() => setPhoneTest(null)}>
          <Suspense fallback={null}>
            <TelephoneTestDrawer
              key={phoneTest.agent?.id}
              agent={phoneTest.agent}
              resource={phoneTest.resource}
              onClose={() => setPhoneTest(null)}
              setError={setError}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {editMember && (
        <ErrorBoundary resetKey={editMember.id} onError={() => setEditMember(null)}>
          <Suspense fallback={null}>
            <AgentEditModal
              key={editMember.id || editMember.label}
              member={editMember}
              setError={setError}
              setInform={setInform}
              onClose={() => setEditMember(null)}
              onSaved={async () => {
                setEditMember(null);
                await reloadSelected();
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {testAgent && (
        <ErrorBoundary resetKey={testAgent.id} onError={() => setTestAgent(null)}>
          <Suspense fallback={null}>
            <TestAgentDrawer
              key={testAgent.id}
              agent={testAgent}
              onResult={(data) => setLastTestResult(data)}
              onClose={() => setTestAgent(null)}
              setError={setError}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  );
}
